import Title from "@/components/common/Title";
import { DiaryLabelChips } from "@/features/diaries/components/DiaryLabelChips";
import { studyByIdQueryOptions } from "@/features/studies/api/selectStudies";
import { StudyTrackingPanel } from "@/features/studies/components/StudyTrackingPanel";
import { useStudiesCopy } from "@/features/i18n/studiesCopy";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import { Button } from "@patient-watch/ui/button";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ClipboardEdit, Send } from "lucide-react";

function StudyAnalyticsPage() {
    const { studyId } = Route.useParams();
    const copy = useStudiesCopy();
    const auth = useAuth();
    const navigate = useNavigate();
    const canEditStudy = Boolean(auth.roles?.isAdmin || auth.isSuperUser);
    const canManageMembers = Boolean(canEditStudy || auth.roles?.isManager);

    const { data: study, isLoading } = useQuery({
        ...studyByIdQueryOptions(studyId),
        retry: false,
    });

    useNav([
        { route: { to: "/studies" }, label: copy.studies },
        {
            route: { to: "/studies/$studyId", params: { studyId } },
            label: study?.study_name ?? copy.studies,
        },
    ]);

    if (isLoading) {
        return (
            <section className="space-y-6 w-full min-w-0">
                <p className="text-muted-foreground text-sm">{copy.loadingStudy}</p>
            </section>
        );
    }

    if (!study) {
        return (
            <section className="space-y-4 w-full min-w-0">
                <Title words={copy.studyNotFound} />
                <Button type="button" variant="outline" onClick={() => void navigate({ to: "/studies" })}>
                    {copy.backToStudies}
                </Button>
            </section>
        );
    }

    const labelNames = study.study_labels?.map((row) => row.diary_labels?.label_name ?? "").filter(Boolean) ?? [];

    return (
        <section className="space-y-6 w-full min-w-0" data-testid="study-analytics-page">
            <div className="flex flex-wrap items-start gap-3">
                <div className="space-y-3 min-w-0">
                    <Title words={study.study_name} description={copy.trackingDescription} />
                    <DiaryLabelChips labelNames={labelNames} />
                </div>
                <div className="flex flex-wrap items-center gap-2 ml-auto shrink-0">
                    <Button
                        type="button"
                        size="md"
                        className="gap-2"
                        data-testid="study-invite-button"
                        aria-label={copy.inviteStudyAria}
                        onClick={() =>
                            void navigate({
                                to: "/studies/$studyId/invite",
                                params: { studyId },
                            })
                        }
                    >
                        <Send className="w-4 h-4" />
                        {copy.invite}
                    </Button>
                    {canEditStudy ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="md"
                            className="gap-2"
                            data-testid="study-edit-button"
                            aria-label={copy.editStudyAria}
                            onClick={() =>
                                void navigate({
                                    to: "/studies/$studyId/edit",
                                    params: { studyId },
                                })
                            }
                        >
                            <ClipboardEdit className="w-4 h-4" />
                            {copy.edit}
                        </Button>
                    ) : null}
                </div>
            </div>

            <StudyTrackingPanel studyId={studyId} hideTitle canManageMembers={canManageMembers} />
        </section>
    );
}

export const Route = createFileRoute("/_app/studies/$studyId/")({
    component: StudyAnalyticsPage,
});
